import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, DollarSign, Users, X, AlertTriangle, Shield } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import RaffleProfitCalculator from './RaffleProfitCalculator';
import type { Organization } from '../../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { toast } from 'react-hot-toast';

type FormData = {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  ticketPrice: string;
  maxTickets: string;
  agreeToTerms: boolean;
};

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

const CreateRaffleForm: React.FC<Props> = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCalculator, setShowCalculator] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    ticketPrice: '',
    maxTickets: '',
    agreeToTerms: false
  });

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data: orgs, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('owner_id', user.id)
        .limit(1)
        .single();

      if (orgError) throw orgError;
      setOrganization(orgs);

      // Only check if organization exists, not its compliance
      if (!orgs) {
        navigate('/onboarding');
        return;
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      setError('Failed to load organization details');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!organization) {
        throw new Error('Organization not found');
      }

      const { error: campaignError } = await supabase
        .from('campaigns')
        .insert([
          {
            org_id: organization.id,
            name: formData.name,
            description: formData.description || null,
            start_date: formData.startDate,
            end_date: formData.endDate,
            ticket_price: parseFloat(formData.ticketPrice),
            max_tickets: formData.maxTickets ? parseInt(formData.maxTickets) : null,
            status: 'draft'
          },
        ]);

      if (campaignError) throw campaignError;

      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  if (!organization) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Create New Raffle</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mb-8">
            <div className="flex">
              <Shield className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Organization Status</h3>
                <p className="text-sm text-blue-700 mt-1">
                  {organization.name} is registered as a {organization.organization_type} organization
                  with {organization.organization_age} years of operation.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end mb-6">
            <button
              onClick={() => setShowCalculator(!showCalculator)}
              className="text-purple-600 hover:text-purple-800 font-medium"
            >
              {showCalculator ? 'Hide Calculator' : 'Show Profit Calculator'}
            </button>
          </div>

          {showCalculator && (
            <div className="mb-8">
              <RaffleProfitCalculator />
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-800 p-4 rounded-md mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raffle Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ticket Price ($)
                </label>
                <input
                  type="number"
                  name="ticketPrice"
                  value={formData.ticketPrice}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Tickets (Optional)
                </label>
                <input
                  type="number"
                  name="maxTickets"
                  value={formData.maxTickets}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    By creating this raffle, you confirm that your organization meets all state and federal requirements 
                    for conducting raffles, including proper registration and reporting obligations. You are responsible 
                    for ensuring compliance with all applicable laws and regulations.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                required
              />
              <label className="ml-2 block text-sm text-gray-700">
                I confirm that my organization is eligible to conduct raffles and agrees to comply with all applicable 
                state and federal regulations, including registration, reporting, and proceeds distribution requirements.
              </label>
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.agreeToTerms}
                className={`px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors ${
                  (loading || !formData.agreeToTerms) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Creating...' : 'Create Raffle'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateRaffleForm;